import { useMediaQuery } from '@mui/material';
import Hls from 'hls.js';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Message, MessageStatus } from 'src/api/messagesApi';
import {
  getMessageDisplayName,
  getMessageThumbnail,
  getMessageVideoUrl,
} from './normalizeCircleMessages';
import styles from './CircleMessages.module.scss';

interface MessageCardProps {
  message: Message;
  onOpen: () => void;
}

const attachPreviewSource = (
  videoElement: HTMLVideoElement,
  videoUrl: string
) => {
  let hls: Hls | null = null;

  if (videoUrl.includes('.m3u8') && Hls.isSupported()) {
    hls = new Hls();
    hls.loadSource(videoUrl);
    hls.attachMedia(videoElement);
  } else {
    videoElement.src = videoUrl;
    videoElement.load();
  }

  return () => {
    hls?.destroy();
    videoElement.pause();
    videoElement.src = '';
  };
};

export const MessageCard = ({ message, onOpen }: MessageCardProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isMobile = useMediaQuery('(max-width:545px)');
  const thumbnail = getMessageThumbnail(message);
  const videoUrl = getMessageVideoUrl(message);
  const [isLoading, setIsLoading] = useState(Boolean(videoUrl) && !thumbnail);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoUrl) {
      return;
    }

    const handleReady = () => {
      setIsLoading(false);
      if (videoElement.currentTime === 0) {
        videoElement.currentTime = 0.05;
      }
    };
    const handleWaiting = () => {
      if (!thumbnail) {
        setIsLoading(true);
      }
    };

    videoElement.addEventListener('canplay', handleReady);
    videoElement.addEventListener('loadeddata', handleReady);
    videoElement.addEventListener('waiting', handleWaiting);

    const detach = attachPreviewSource(videoElement, videoUrl);

    return () => {
      videoElement.removeEventListener('canplay', handleReady);
      videoElement.removeEventListener('loadeddata', handleReady);
      videoElement.removeEventListener('waiting', handleWaiting);
      detach();
    };
  }, [thumbnail, videoUrl]);

  const handleMouseEnter = () => {
    if (isMobile || !videoRef.current) {
      return;
    }

    videoRef.current.play().catch(() => undefined);
  };

  const handleMouseLeave = () => {
    if (isMobile || !videoRef.current) {
      return;
    }

    videoRef.current.pause();
  };

  return (
    <button
      type="button"
      className={styles.card}
      onClick={onOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {videoUrl ? (
        <video
          ref={videoRef}
          className={styles.preview}
          poster={thumbnail || undefined}
          muted
          playsInline
          preload="metadata"
        />
      ) : thumbnail ? (
        <div
          className={styles.preview}
          style={{ backgroundImage: `url(${thumbnail})` }}
        />
      ) : (
        <div className={styles.placeholder}>
          {message.status === MessageStatus.PROCESSING
            ? 'Processing video…'
            : 'No preview'}
        </div>
      )}
      {isLoading && <div className={styles.previewLoader} />}
      <div className={styles.overlay}>
        <div className={styles.userName}>{getMessageDisplayName(message)}</div>
        {message.createdAt && (
          <div className={styles.time}>
            {moment(message.createdAt).fromNow()}
          </div>
        )}
        {message.title && <div className={styles.title}>{message.title}</div>}
      </div>
    </button>
  );
};
